import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import BlogCover from "../components/blog/BlogCover";
import SEOHead from "../components/SEOHead";
import { getBlogPost, formatPostDate, postDate, postReadTime } from "../lib/blogApi";

/**
 * Very small markdown-ish renderer just for the blog body.
 * Supports h2 (##), h3 (###), unordered list (- ...), ordered list (1. ...),
 * paragraphs, and **bold** inside paragraphs. Anything else falls through
 * as a plain paragraph. Deliberately simple — no HTML-injection surface.
 */
function renderBody(text) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3) });
      continue;
    }
    if (line.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h3", text: line.slice(4) });
      continue;
    }
    const ulMatch = /^[-*]\s+(.*)/.exec(line);
    if (ulMatch) {
      flushParagraph();
      if (!list || list.type !== "ul") {
        flushList();
        list = { type: "ul", items: [] };
      }
      list.items.push(ulMatch[1]);
      continue;
    }
    const olMatch = /^\d+\.\s+(.*)/.exec(line);
    if (olMatch) {
      flushParagraph();
      if (!list || list.type !== "ol") {
        flushList();
        list = { type: "ol", items: [] };
      }
      list.items.push(olMatch[1]);
      continue;
    }
    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();

  return blocks.map((b, i) => {
    if (b.type === "h2") {
      return (
        <h2
          key={i}
          className="font-display text-2xl sm:text-3xl font-extrabold text-moss-950 leading-tight pt-6"
        >
          {b.text}
        </h2>
      );
    }
    if (b.type === "h3") {
      return (
        <h3
          key={i}
          className="font-display text-xl sm:text-2xl font-bold text-moss-950 leading-tight pt-4"
        >
          {b.text}
        </h3>
      );
    }
    if (b.type === "ul") {
      return (
        <ul
          key={i}
          className="space-y-2.5 pl-5 list-disc marker:text-sage-500"
        >
          {b.items.map((it, j) => (
            <li
              key={j}
              className="text-base sm:text-lg text-moss-800 leading-relaxed"
            >
              {renderInline(it)}
            </li>
          ))}
        </ul>
      );
    }
    if (b.type === "ol") {
      return (
        <ol
          key={i}
          className="space-y-2.5 pl-5 list-decimal marker:text-sage-500 marker:font-bold"
        >
          {b.items.map((it, j) => (
            <li
              key={j}
              className="text-base sm:text-lg text-moss-800 leading-relaxed pl-1"
            >
              {renderInline(it)}
            </li>
          ))}
        </ol>
      );
    }
    return (
      <p
        key={i}
        className="text-base sm:text-lg text-moss-800 leading-[1.75]"
      >
        {renderInline(b.text)}
      </p>
    );
  });
}

// **bold** support inside paragraphs / list items.
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="text-moss-950">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    ),
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPost(null);
    setError(null);
    getBlogPost(slug)
      .then((p) => {
        setPost(p);
        window.scrollTo({ top: 0 });
      })
      .catch((e) => setError(e.response?.status === 404 ? 404 : "error"));
  }, [slug]);

  if (error === 404) return <Navigate to="/blog" replace />;

  const canonicalUrl = `https://binshare.co.uk/blog/${slug}`;
  const publishDate = post ? postDate(post) : null;
  // Prefer server-rendered HTML if the CMS provided it; otherwise fall back
  // to our lightweight markdown renderer over contentMarkdown / content / body.
  const rawHtml = post?.contentHtml;
  const markdownSource =
    post?.contentMarkdown || post?.content || post?.body || "";
  const coverForSchema =
    post?.featuredImage || post?.featured_image || post?.coverImage;
  const articleJsonLd = post && {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: coverForSchema
      ? [coverForSchema]
      : ["https://binshare.co.uk/icon-512.png"],
    datePublished: publishDate,
    dateModified: publishDate,
    author: {
      "@type": "Organization",
      name: post.author || "Binshare Team",
      url: "https://binshare.co.uk/",
    },
    publisher: {
      "@type": "Organization",
      name: "Binshare",
      logo: {
        "@type": "ImageObject",
        url: "https://binshare.co.uk/icon-512.png",
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
  };
  const breadcrumbJsonLd = post && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://binshare.co.uk/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://binshare.co.uk/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: canonicalUrl },
    ],
  };
  // Cap description at 160 chars for optimal SERP display.
  const seoDescription = post?.excerpt
    ? post.excerpt.length > 160
      ? post.excerpt.slice(0, 157) + "..."
      : post.excerpt
    : "";

  return (
    <div
      className="min-h-screen bg-cream-50 text-moss-900"
      data-testid="blog-article-page"
    >
      <SEOHead
        path={`/blog/${slug}`}
        title={post ? `${post.title} | Binshare Blog` : "Binshare Blog"}
        description={seoDescription}
        image={coverForSchema || "https://binshare.co.uk/icon-512.png"}
        ogType="article"
        keywords={
          post
            ? [
                post.category?.toLowerCase(),
                "waste disposal UK",
                "bin collection service",
                "waste removal UK",
                "skip hire alternative",
                ...(post.tags || []),
              ]
                .filter(Boolean)
                .join(", ")
            : null
        }
        jsonLd={post ? [articleJsonLd, breadcrumbJsonLd] : null}
      />
      <Navbar />
      <main className="pt-24 md:pt-32 pb-24">
        <article className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-moss-700 hover:text-sage-700 transition-colors"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to the journal
          </Link>

          {!post && !error && (
            <div
              className="mt-10 animate-pulse space-y-4"
              data-testid="blog-article-loading"
            >
              <div className="h-4 w-40 bg-moss-100 rounded" />
              <div className="h-12 w-full bg-moss-100 rounded" />
              <div className="h-6 w-3/4 bg-moss-100 rounded" />
              <div className="aspect-[16/9] w-full bg-moss-100 rounded-3xl mt-8" />
            </div>
          )}

          {post && (
            <>
              <div className="mt-8 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-sage-100 text-[10px] font-bold tracking-[0.14em] uppercase text-sage-800">
                  {post.category}
                </span>
                <time dateTime={publishDate} className="text-sm text-moss-500">
                  {formatPostDate(publishDate)}
                </time>
                <span aria-hidden className="text-moss-300">·</span>
                <span className="inline-flex items-center gap-1 text-sm text-moss-500">
                  <Clock className="w-3.5 h-3.5" /> {postReadTime(post)}
                </span>
              </div>

              <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-moss-950 leading-[1.05] text-balance">
                {post.title}
              </h1>
              <p className="mt-5 text-lg sm:text-xl text-moss-700 leading-relaxed">
                {post.excerpt}
              </p>
              <p className="mt-4 text-sm text-moss-500">
                By{" "}
                <span className="font-semibold text-moss-950">{post.author}</span>
              </p>

              <div className="mt-10">
                <BlogCover post={post} className="aspect-[16/9] rounded-3xl" />
              </div>

              <div
                className="mt-8 space-y-6"
                data-testid="blog-article-body"
              >
                {rawHtml ? (
                  <div
                    className="prose-article"
                    // Content ships from our own backend; we accept the HTML
                    // as authored in the CMS.
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: rawHtml }}
                  />
                ) : (
                  renderBody(markdownSource)
                )}
              </div>

              <div className="mt-16 rounded-3xl bg-moss-950 text-cream-50 px-7 py-8">
                <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-sage-300">
                  Ready to try it?
                </p>
                <p className="font-display mt-2 text-xl sm:text-2xl font-extrabold leading-snug">
                  Snap. Quote. Gone — clear your first job in minutes.
                </p>
                <Link
                  to="/#get-the-app"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cream-50 text-moss-950 text-sm font-semibold hover:bg-cream-100 transition-colors"
                  data-testid="blog-article-cta"
                >
                  Get the app
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
}
